import { API_ENDPOINTS, API_CONFIG } from '../config/api.config';
import { useAuthStore } from '../../features/auth/store/authStore';
import { botService } from './BotService';
import { httpClient, safeBigInts } from '../services/HttpClient';
import type {
  BotItem,
  BotCapabilities,
} from '../../features/assistant-hub/types';
import type {
  Bot,
  ChatRequest,
  Model,
  SSEStreamEvent,
  SSEEvent,
} from '../types/api.types';
import type { DeepResearchUpdate } from '../types/chat.types';
import { now } from '@/shared/utils/dateUtils';

/**
 * Service for managing chat sessions and bot interactions
 */
export class ChatSessionService {
  /**
   * Initialize a new chat session for the given bot
   * Based on the API documentation, sessions are created when the first message is sent
   * So we generate a unique session ID here and let the first message establish it
   */
  static async initializeChatSession(botId: string): Promise<string> {
    try {
      // Generate a unique session ID - format similar to your curl example
      const sessionId = crypto.randomUUID();

      return sessionId;
    } catch (error) {
      console.error(
        '❌ [ChatSessionService] Failed to generate session ID:',
        error
      );
      // Fallback to timestamp-based ID
      return `session_${botId}_${now()}`;
    }
  }

  /**
   * Determine the appropriate chat endpoint based on bot capabilities
   * Now always returns CHAT_STREAM as all capabilities are handled via config flags
   */
  static determineChatEndpoint(_bot: BotItem): string {
    // All chat types now use the same endpoint with different config flags

    return API_ENDPOINTS.CHAT_STREAM;
  }

  /**
   * Enhance bot item with session and capability information
   */
  static async enhanceBotForChat(bot: BotItem): Promise<BotItem> {
    // Always fetch enhanced bot details first - this is the most important part
    const enhancedBotDetails = await botService.getEnhancedBotDetails(bot.id);

    // Determine capabilities based on actual bot data
    const capabilities = await this.determineBotCapabilities(
      bot.id,
      enhancedBotDetails
    );

    // Determine chat endpoint based on capabilities
    const chatEndpoint = this.determineChatEndpoint({ ...bot, capabilities });

    // Try to initialize session, but don't let it block the bot enhancement
    let sessionId: string;
    try {
      sessionId = await this.initializeChatSession(bot.id);
    } catch {
      // Generate a session ID for fallback - this will work with the ChatSessionService
      sessionId = `session_${bot.id}_${now()}`;
    }

    return {
      ...bot,
      sessionId,
      capabilities,
      chatEndpoint,
      ragEnabled: capabilities.rag,
      multimodalEnabled: capabilities.multimodal,
      webSearchEnabled: capabilities.webSearch,
      webMediaEnabled: capabilities.webMedia,
      // CRITICAL: Always preserve enhanced bot details
      enhancedBotDetails: enhancedBotDetails || undefined,
    };
  }

  /**
   * Determine bot capabilities based on actual bot data from API
   */
  private static async determineBotCapabilities(
    botId: string,
    enhancedBotDetails: Bot | null
  ): Promise<BotCapabilities> {
    try {
      // Primary check: actual datastore field from the API
      const hasDatastoreFromBot =
        enhancedBotDetails?.datastore &&
        Array.isArray(enhancedBotDetails.datastore) &&
        enhancedBotDetails.datastore.length > 0;

      // Secondary check: fetch datastore via service (backup)
      let dataStores: string[] = [];
      try {
        dataStores = await botService.getBotDataStores(botId);
      } catch {
        // catch error
      }

      // Tertiary check: name/description patterns for specialized bots
      const isSpecializedBot =
        (enhancedBotDetails?.bot_name?.toLowerCase() || '').includes('hr') ||
        (enhancedBotDetails?.bot_name?.toLowerCase() || '').includes('ask') ||
        (enhancedBotDetails?.description?.toLowerCase() || '').includes('hr') ||
        (enhancedBotDetails?.description?.toLowerCase() || '').includes(
          'documents'
        ) ||
        (enhancedBotDetails?.description?.toLowerCase() || '').includes(
          'knowledge'
        );

      // RAG is enabled if we have datastores OR it's a specialized bot
      const ragEnabled =
        hasDatastoreFromBot || dataStores.length > 0 || isSpecializedBot;

      // For other capabilities, we can infer from description for now
      // In a full implementation, these would be explicit fields in the bot data
      const description = enhancedBotDetails?.description?.toLowerCase() || '';
      const title = enhancedBotDetails?.bot_name?.toLowerCase() || '';

      const isMultimodal =
        description.includes('image') ||
        description.includes('multimodal') ||
        description.includes('vision') ||
        title.includes('image') ||
        title.includes('multimodal') ||
        title.includes('vision');

      const hasWebSearch =
        description.includes('web') ||
        description.includes('search') ||
        description.includes('internet') ||
        title.includes('web') ||
        title.includes('search') ||
        title.includes('internet');

      const hasWebMedia =
        description.includes('media') ||
        description.includes('video') ||
        description.includes('audio') ||
        title.includes('media') ||
        title.includes('video') ||
        title.includes('audio');

      const capabilities: BotCapabilities = {
        rag: ragEnabled,
        multimodal: isMultimodal,
        webSearch: hasWebSearch,
        webMedia: hasWebMedia,
        streaming: API_CONFIG.ENABLE_STREAMING,
      };

      return capabilities;
    } catch (error) {
      console.error('Failed to determine bot capabilities:', error);

      // Fallback to basic capabilities
      return {
        rag: false,
        multimodal: false,
        webSearch: false,
        webMedia: false,
        streaming: API_CONFIG.ENABLE_STREAMING,
      };
    }
  }

  /**
   * Clean up chat session
   */
  static async cleanupChatSession(
    sessionId: string,
    botId: string
  ): Promise<void> {
    try {
      const authToken = useAuthStore.getState().token;
      await fetch(API_ENDPOINTS.DELETE_CONVERSATION(sessionId), {
        method: 'DELETE',
        headers: {
          'assistant-id': botId,
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
      });
    } catch {
      // Don't throw error as this is cleanup
    }
  }

  /**
   * Send multimodal message with file attachments using streaming endpoint
   * Supports two modes:
   * 1. Raw files (files array) - for fresh uploads
   * 2. GCS URLs (fileUrls array) - for retry when files are no longer in local storage
   */
  static async sendMultimodalMessageStream(
    message: string,
    sessionId: string,
    botId: string,
    files: File[],
    botDetails?: Bot | null,
    isWebSearchEnabled?: boolean,
    currentModel?: Model | null,
    callbacks?: {
      onChunk: (chunk: string) => void;
      onComplete: (conversationId?: string) => void;
      onError: (error: Error) => void;
      onSessionStart?: (conversationId: string) => void;
      onDeepResearchUpdate?: (update: DeepResearchUpdate) => void;
      onWebSearchMetadata?: (sources: unknown[]) => void;
      onRagMetadata?: (sources: unknown[]) => void;
      // TODO: Uncomment when backend message_ids streaming is deployed
      // onMessageIds?: (
      //   userMessageId: string | null,
      //   aiMessageId: string | null
      // ) => void;
    },
    _chatSource?: 'sidebar' | 'assistant' | null,
    fileUrls?: string[] // GCS URLs for retry functionality
  ): Promise<() => void> {
    // Determine if we're using GCS URLs (retry mode) or raw files (fresh upload)
    const useGcsUrls = fileUrls && fileUrls.length > 0;
    // Only validate files if we're not using GCS URLs
    if (!useGcsUrls) {
      // Validate file content before sending
      for (const file of files) {
        if (file.size === 0) {
          console.error(
            `[ChatSessionService] File "${file.name}" has zero size!`
          );
          callbacks?.onError?.(new Error(`File "${file.name}" is empty`));
          return () => {};
        }

        // Try to read a small chunk to verify file is readable
        try {
          const slice = file.slice(0, Math.min(100, file.size));
          const reader = new FileReader();

          await new Promise<void>((resolve, reject) => {
            reader.onload = () => {
              resolve();
            };
            reader.onerror = () => {
              console.error(
                `[ChatSessionService] Failed to read file "${file.name}":`,
                reader.error
              );
              reject(reader.error);
            };
            reader.readAsArrayBuffer(slice);
          });
        } catch (error) {
          console.error(
            `[ChatSessionService] Error validating file "${file.name}":`,
            error
          );
          callbacks?.onError?.(new Error(`Failed to read file "${file.name}"`));
          return () => {};
        }
      }
    }

    // Get bot configuration for RAG and system prompt
    const enhancedBotDetails =
      botDetails || (await botService.getEnhancedBotDetails(botId));

    const systemPrompt =
      enhancedBotDetails?.system_instructions || 'You are a helpful assistant.';
    const temperature = enhancedBotDetails?.temperature || 0.7;
    const kValue = enhancedBotDetails?.k_value || 6;

    // Check if bot has datastore for RAG capability
    const hasDatastore =
      enhancedBotDetails?.datastore &&
      Array.isArray(enhancedBotDetails.datastore) &&
      enhancedBotDetails.datastore.length > 0;

    // Use the actual datastore IDs from the bot details
    const dataStoreIds = hasDatastore
      ? (enhancedBotDetails!.datastore as string[])
      : [];

    // Use the web search flag passed from the chat store
    const hasWebSearch = isWebSearchEnabled || false;
    const webSearchEnabled = hasWebSearch;
    // Determine rag_enabled based on bot type
    // rag_enabled should be true only when bot exists and is not a default bot
    const ragEnabled = Boolean(
      enhancedBotDetails && !enhancedBotDetails.is_default
    );

    // Use currentModel if provided, otherwise fall back to default
    const modelProvider = currentModel?.modelProvider || 'anthropic';
    const modelName = currentModel?.modelName || 'claude-sonnet-4@20250514';

    // Use the multimodal endpoint
    const endpoint = API_ENDPOINTS.CHAT_MULTIMODAL_STREAM;

    // Prepare the data for multipart/form-data
    // The HttpClient will extract 'files' and stringify the rest as json_input
    // For GCS URLs (retry mode), pass file_urls instead of files
    const requestData = useGcsUrls
      ? {
          question: message,
          config: {
            model_provider: modelProvider,
            model_name: modelName,
            conversation_id: sessionId,
            temperature: temperature,
            system_prompt: systemPrompt,
            rag_enabled: ragEnabled,
            web_search_enabled: webSearchEnabled,
            k: hasDatastore ? kValue : undefined,
            store_ids: hasDatastore ? dataStoreIds : undefined,
          },
          file_urls: fileUrls.join(','), // Comma-separated GCS URLs for backend
        }
      : {
          question: message,
          config: {
            model_provider: modelProvider,
            model_name: modelName,
            conversation_id: sessionId,
            temperature: temperature,
            system_prompt: systemPrompt,
            rag_enabled: ragEnabled,
            web_search_enabled: webSearchEnabled,
            k: hasDatastore ? kValue : undefined,
            store_ids: hasDatastore ? dataStoreIds : undefined,
          },
          files, // Will be extracted by HttpClient and sent as separate form fields
        };

    // Create abort controller for cancellation
    const abortController = new AbortController();
    let isCancelled = false;

    // Get user info for headers
    const user = useAuthStore.getState().user;

    // Prepare custom headers including assistant-id
    // Note: Authorization header is automatically added by HttpClient.getHeaders()
    const customHeaders: HeadersInit = {
      'assistant-id': botId,
      'user-id': user?.email || user?.uid || 'anonymous',
    };

    // Start streaming with multipart/form-data
    const streamPromise = httpClient.streamPostWithFiles(
      endpoint,
      requestData,
      (data: unknown) => {
        if (isCancelled) return;

        // Handle both new and legacy SSE event formats
        const event = data as SSEStreamEvent | SSEEvent;

        // More robust type checking - handle both explicit type field and inferred structure
        const hasExplicitType =
          typeof event === 'object' &&
          event !== null &&
          'type' in event &&
          typeof event.type === 'string' &&
          event.type.length > 0;
        const hasSessionStartStructure =
          typeof event === 'object' &&
          event !== null &&
          'conversation_id' in event &&
          !('content' in event) &&
          !('answer' in event) &&
          !('complete' in event);

        // Check if it's the new format with type field or has session_start structure
        if (hasExplicitType || hasSessionStartStructure) {
          const typedEvent = event as SSEStreamEvent;
          // If no explicit type but has session_start structure, infer it
          const eventType =
            typedEvent.type ||
            (hasSessionStartStructure ? 'session_start' : 'unknown');

          switch (eventType) {
            case 'session_start': {
              const conversationId = (
                typedEvent as { conversation_id?: string }
              ).conversation_id;
              if (conversationId) {
                callbacks?.onSessionStart?.(conversationId);
              }
              break;
            }
            case 'token': {
              // Extract text from content array if it's an array
              const tokenEvent = typedEvent as {
                content?: string | Array<{ text?: string }>;
              };
              if (Array.isArray(tokenEvent.content)) {
                const text = tokenEvent.content
                  .map((item: { text?: string }) => item.text || '')
                  .join('');
                callbacks?.onChunk(text);
              } else if (tokenEvent.content) {
                callbacks?.onChunk(tokenEvent.content);
              }
              break;
            }
            // TODO: Uncomment when backend message_ids streaming is deployed
            // case 'message_ids': {
            //   const { user_message_id, ai_message_id } = typedEvent;
            //   console.log('[ChatSessionService] Received message IDs:', {
            //     user_message_id,
            //     ai_message_id,
            //   });
            //   callbacks?.onMessageIds?.(user_message_id, ai_message_id);
            //   break;
            // }
            // case 'web_search_metadata':
            case 'web_sources': {
              // Web search sources received from stream
              const webData = typedEvent as {
                web_search_sources?: unknown[];
                sources?: unknown[];
              };
              const sources =
                webData.web_search_sources || webData.sources || [];
              callbacks?.onWebSearchMetadata?.(sources);
              break;
            }
            // case 'rag_metadata': {
            //   RAG sources received from stream
            //   const ragData = typedEvent as { sources?: unknown[] };
            //   const sources = ragData.sources || [];
            //   callbacks?.onRagMetadata?.(sources);
            //   break;
            // }
            case 'complete':
              // Stream is complete
              break;
          }
        }
        // Legacy format handling
        else {
          const legacyEvent = event as SSEEvent;
          if ('answer' in legacyEvent) {
            callbacks?.onChunk(legacyEvent.answer);
          } else if ('run_id' in legacyEvent) {
            callbacks?.onSessionStart?.(legacyEvent.run_id);
          }
          // FALLBACK: Check for session_start by structure (no type field, multimodal)
          else if (
            'conversation_id' in legacyEvent &&
            'user_id' in legacyEvent &&
            Object.keys(legacyEvent).length <= 3
          ) {
            const conversationId = (legacyEvent as { conversation_id: string })
              .conversation_id;
            if (conversationId) {
              callbacks?.onSessionStart?.(conversationId);
            }
          }
        }
      },
      (error: Error) => {
        if (!isCancelled) {
          callbacks?.onError?.(error);
        }
      },
      false, // isRetry
      {
        fileFieldName: 'files', // Use 'files' as the field name for all files
        jsonFieldName: 'json_input', // Use 'json_input' for the ChatRequest JSON
        customHeaders, // Include assistant-id and user-id headers
      }
    );

    streamPromise
      .then(() => {
        if (!isCancelled) {
          callbacks?.onComplete?.();
        }
      })
      .catch((error: Error) => {
        if (!isCancelled) {
          callbacks?.onError?.(error);
        }
      });

    // Return cancel function
    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }

  /**
   * Send message to the appropriate chat endpoint with proper bot context
   * If files are attached, uses the multimodal streaming endpoint instead
   */
  static async sendMessage(
    message: string,
    sessionId: string,
    endpoint: string,
    botId: string,
    attachments?: File[],
    botDetails?: Bot | null,
    isWebSearchEnabled?: boolean,
    currentModel?: Model | null,
    _chatSource?: 'sidebar' | 'assistant' | null
  ): Promise<Response> {
    // If files are attached, we need to use the multimodal endpoint
    // However, since sendMessage returns a Response and the caller expects to use handleStreamingResponse,
    // we'll still use the regular endpoint but convert files to data URLs
    // For proper multimodal support with streaming, use sendMultimodalMessageStream instead

    const authToken = useAuthStore.getState().token;
    const user = useAuthStore.getState().user;

    // Get bot configuration for RAG and system prompt
    const enhancedBotDetails =
      botDetails || (await botService.getEnhancedBotDetails(botId));

    const systemPrompt =
      enhancedBotDetails?.system_instructions || 'You are a helpful assistant.';
    const temperature = enhancedBotDetails?.temperature || 0.7;
    const kValue = enhancedBotDetails?.k_value || 6; // Default to 6 like in your curl example

    // Check if bot has datastore for RAG capability
    const hasDatastore =
      enhancedBotDetails?.datastore &&
      Array.isArray(enhancedBotDetails.datastore) &&
      enhancedBotDetails.datastore.length > 0;

    // Use the actual datastore IDs from the bot details
    const dataStoreIds = hasDatastore
      ? (enhancedBotDetails!.datastore as string[])
      : [];

    // Use the web search flag passed from the chat store instead of auto-detection
    const hasWebSearch = isWebSearchEnabled || false;

    // Convert attachments to file URLs if needed
    let fileUrls: string[] | undefined;
    if (attachments && attachments.length > 0) {
      // Process attachments to data URLs (in production, these would be uploaded to get real URLs)
      const processedFiles = await this.processAttachments(attachments);
      fileUrls = processedFiles.map(
        file => `data:${file.mime_type};base64,${file.file_data}`
      );
    }

    // Determine flags based on web search and bot type
    // rag_enabled should be true only when bot exists and is not a default bot
    const webSearchEnabled = hasWebSearch;
    const ragEnabled = Boolean(
      enhancedBotDetails && !enhancedBotDetails.is_default
    );

    // Use currentModel if provided, otherwise fall back to default
    const modelProvider = currentModel?.modelProvider || 'anthropic';
    const modelName = currentModel?.modelName || 'claude-sonnet-4@20250514';

    // Build the new simplified request structure
    const body: ChatRequest = {
      question: message,
      config: {
        model_provider: modelProvider,
        model_name: modelName,
        conversation_id: sessionId,
        temperature: temperature,
        system_prompt: systemPrompt,
        rag_enabled: ragEnabled,
        web_search_enabled: webSearchEnabled,
        k: hasDatastore ? kValue : undefined,
        store_ids: hasDatastore ? dataStoreIds : undefined,
      },
      file_urls: fileUrls,
    };

    // console.log('📦 [ChatSessionService] Final payload:', body);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'user-id': user?.email || user?.uid || 'anonymous',
        'assistant-id': botId,
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.error('❌ [ChatSessionService] Request failed:', {
        status: response.status,
        statusText: response.statusText,
        endpoint,
      });
    }

    // console.log('✅ [ChatSessionService] Request sent');
    return response;
  }

  /**
   * Send deep research message to the deep research endpoint
   */
  static async sendDeepResearchMessage(
    topic: string,
    sessionId: string,
    botId: string,
    config: Record<string, unknown> = {},
    conversationId?: string,
    assistantId?: string,
    userId?: string
  ): Promise<Response> {
    const authToken = useAuthStore.getState().token;
    const user = useAuthStore.getState().user;
    const currentUserId = userId || user?.email || user?.uid || 'anonymous';
    const currentAssistantId = assistantId || botId;
    const currentConversationId = conversationId || sessionId;

    const body = {
      topic,
      config,
    };

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: authToken ? `Bearer ${authToken}` : '',
      ...(currentConversationId && {
        'conversation-id': currentConversationId,
      }),
      ...(currentAssistantId && { 'assistant-id': currentAssistantId }),
      ...(currentUserId && { 'user-id': currentUserId }),
    };

    const response = await fetch(API_ENDPOINTS.DEEP_RESEARCH_STREAM, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.error('❌ [ChatSessionService] Deep research request failed:', {
        status: response.status,
        statusText: response.statusText,
      });
    }

    return response;
  }

  /**
   * Process file attachments for multimodal endpoints
   */
  private static async processAttachments(files: File[]): Promise<
    Array<{
      file_name: string;
      file_data: string;
      mime_type: string;
    }>
  > {
    const processedFiles: Array<{
      file_name: string;
      file_data: string;
      mime_type: string;
    }> = [];

    for (const file of files) {
      try {
        const base64Data = await this.fileToBase64(file);
        processedFiles.push({
          file_name: file.name,
          file_data: base64Data,
          mime_type: file.type,
        });
      } catch (error) {
        console.error('Failed to process attachment:', file.name, error);
      }
    }

    return processedFiles;
  }

  /**
   * Convert file to base64 string
   */
  private static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Handle streaming response with background tab support
   * Uses aggressive read loop that continues even when tab is hidden
   */
  static async handleStreamingResponse(
    response: Response,
    onChunk: (chunk: string) => void,
    onComplete: (conversationId?: string) => void,
    onError: (error: Error) => void,
    onSessionStart?: (conversationId: string) => void,
    onToolStart?: (tool: string, content: string) => void,
    onToolEnd?: (tool: string, content: string) => void,
    onThinking?: (content: string, node?: string) => void,
    onWebSearchMetadata?: (sources: unknown[]) => void,
    onRagMetadata?: (sources: unknown[]) => void,
    onDeepResearchUpdate?: (update: DeepResearchUpdate) => void
    // TODO: Uncomment when backend message_ids streaming is deployed
    // onMessageIds?: (
    //   userMessageId: string | null,
    //   aiMessageId: string | null
    // ) => void
  ): Promise<void> {
    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    // Track visibility state for background tab handling
    let _isTabVisible = !document.hidden;

    // Visibility change handler - stream reading continues automatically
    const visibilityHandler = () => {
      _isTabVisible = !document.hidden;
    };

    document.addEventListener('visibilitychange', visibilityHandler);

    try {
      let isStreamingComplete = false;
      let buffer = '';
      let sessionConversationId: string | undefined;

      // Background-aware read loop
      // Uses recursive setTimeout to keep reading even when tab is hidden/throttled
      // The key is that reader.read() will still work, we just need to keep calling it
      const readLoop = async (): Promise<void> => {
        if (isStreamingComplete) {
          return;
        }

        try {
          // Read from stream - this will work even when tab is hidden
          // Browsers may throttle setTimeout, but fetch streams continue to work
          const { done, value } = await reader.read();

          if (done) {
            isStreamingComplete = true;
            document.removeEventListener('visibilitychange', visibilityHandler);
            onComplete(sessionConversationId);
            return;
          }

          // Process chunk immediately, even if tab is hidden
          // This ensures data is accumulated even when UI is throttled
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                let dataContent = line.slice(6); // Remove first "data: "

                // Check if there's a double "data: " prefix (new format)
                if (dataContent.startsWith('data: ')) {
                  dataContent = dataContent.slice(6); // Remove second "data: "
                }

                // Skip empty data
                if (!dataContent.trim()) continue;

                // Fix: integers with 16+ digits exceed Number.MAX_SAFE_INTEGER and
                // are silently corrupted by JSON.parse. safeBigInts converts them to strings.
                const data = JSON.parse(safeBigInts(dataContent));

                // Handle Deep Research specific events (event field)
                if (data.event) {
                  switch (data.event) {
                    case 'start':
                    case 'update':
                    case 'web_sources':
                    case 'complete':
                      onDeepResearchUpdate?.({
                        type: data.event as
                          | 'start'
                          | 'update'
                          | 'web_sources'
                          | 'complete',
                        data: data,
                      });
                      break;
                  }
                }

                // Handle both 'type' field (new format) and 'event' field (deep research format)
                // Also detect session_start by structure for multimodal compatibility
                const hasSessionStartStructure =
                  'conversation_id' in data &&
                  !('content' in data) &&
                  !('answer' in data) &&
                  !('complete' in data);

                // Determine event type (check both 'type' and 'event' fields for compatibility)
                const eventType =
                  data.type ||
                  data.event ||
                  (hasSessionStartStructure ? 'session_start' : null);

                if (eventType) {
                  switch (eventType) {
                    case 'session_start':
                      // Capture the conversation ID from the server
                      sessionConversationId = data.conversation_id;
                      // Call the callback if provided
                      if (onSessionStart && sessionConversationId) {
                        onSessionStart(sessionConversationId);
                      }
                      break;
                    case 'token':
                      // Extract text from content array
                      if (Array.isArray(data.content)) {
                        const text = data.content
                          .map(item => item.text || '')
                          .join('');
                        onChunk(text);
                      } else {
                        onChunk(data.content);
                      }
                      break;
                    // TODO: Uncomment when backend message_ids streaming is deployed
                    // case 'message_ids':
                    //   console.log('[ChatSessionService] Received message IDs:', {
                    //     user_message_id: data.user_message_id,
                    //     ai_message_id: data.ai_message_id,
                    //   });
                    //   onMessageIds?.(data.user_message_id, data.ai_message_id);
                    //   break;
                    case 'complete':
                      // Stream is complete
                      // CRITICAL FIX: For deep research, answer comes in complete event
                      // Extract answer BEFORE calling onComplete to ensure accumulatedText is populated
                      if (
                        data.answer !== undefined &&
                        data.answer !== null &&
                        data.answer !== ''
                      ) {
                        // Deep research sends answer in complete event - extract it first
                        const answerText =
                          typeof data.answer === 'string'
                            ? data.answer
                            : String(data.answer);
                        if (answerText.trim().length > 0) {
                          onChunk(answerText);
                        }
                      }
                      // Handle web search sources if present in complete event
                      if (
                        data.web_search_sources &&
                        Array.isArray(data.web_search_sources)
                      ) {
                        onWebSearchMetadata?.(data.web_search_sources);
                      }
                      // Capture conversation_id from complete event if not already set
                      // Some backends send the conversation_id in the complete event
                      if (
                        !sessionConversationId &&
                        data.conversation_id &&
                        typeof data.conversation_id === 'string'
                      ) {
                        sessionConversationId = data.conversation_id;
                        if (onSessionStart) {
                          onSessionStart(sessionConversationId || '');
                        }
                      }
                      // Now mark stream as complete
                      isStreamingComplete = true;
                      onComplete(sessionConversationId);
                      break;
                    case 'tool_start':
                      // Tool invocation started
                      {
                        const toolName = data.tool || 'unknown_tool';
                        const toolStartContent =
                          data.content || `Using ${toolName}...`;
                        onToolStart?.(toolName, toolStartContent);
                      }
                      break;
                    case 'tool_end':
                      // Tool invocation completed
                      {
                        const toolEndName = data.tool || 'unknown_tool';
                        const toolEndContent =
                          data.content || `Finished using ${toolEndName}`;
                        onToolEnd?.(toolEndName, toolEndContent);
                      }
                      break;
                    case 'thinking':
                      // AI is thinking/planning
                      {
                        const thinkingContent = data.content || '';
                        const thinkingNode = data.node;
                        onThinking?.(thinkingContent, thinkingNode);
                      }
                      break;
                    case 'web_search_metadata':
                    case 'web_sources':
                      // Web search sources received from stream
                      {
                        const sources =
                          data.web_search_sources || data.sources || [];
                        onWebSearchMetadata?.(sources);
                      }
                      break;
                    case 'rag_metadata':
                      // RAG sources received from stream
                      {
                        const sources = data.sources || [];
                        onRagMetadata?.(sources);
                      }
                      break;
                    case 'update':
                      // Deep research progress update (optional - can be ignored)
                      // These are sent during research processing but don't contain answer text
                      break;
                    case 'error':
                      // Error event from deep research
                      console.error(
                        '[ChatSessionService] Stream error event:',
                        data.error
                      );
                      onError(new Error(data.error || 'Stream error'));
                      break;
                  }
                }
                // Handle legacy formats for backward compatibility
                // Only process if we didn't already handle it above (no type/event field)
                else if (!eventType) {
                  if (data.answer !== undefined) {
                    onChunk(data.answer);
                  } else if (data.content !== undefined) {
                    onChunk(data.content);
                  } else if (typeof data === 'string') {
                    onChunk(data);
                  }
                }
                // FALLBACK: Check for session_start by structure (no type field)
                else if (
                  'conversation_id' in data &&
                  'user_id' in data &&
                  Object.keys(data).length <= 3
                ) {
                  sessionConversationId = data.conversation_id;
                  if (onSessionStart && sessionConversationId) {
                    onSessionStart(sessionConversationId);
                  }
                }
              } catch {
                // catch error
              }
            }
          }

          // Continue reading - use setTimeout(0) to keep event loop active
          // This ensures reading continues even when tab is hidden/throttled
          // The browser may throttle setTimeout, but reader.read() will still work
          setTimeout(() => {
            void readLoop();
          }, 0);
        } catch (readError) {
          // Error reading from stream - stop and report
          isStreamingComplete = true;
          document.removeEventListener('visibilitychange', visibilityHandler);
          onError(
            readError instanceof Error
              ? readError
              : new Error('Stream reading error')
          );
        }
      };

      // Start the read loop
      void readLoop();
    } catch (error) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      onError(error instanceof Error ? error : new Error('Streaming error'));
    }
  }

  /**
   * Transcribe audio using voice dictation API
   */
  static async transcribeVoice(
    audioBlob: Blob,
    userId?: string
  ): Promise<string> {
    const authToken = useAuthStore.getState().token;
    const user = useAuthStore.getState().user;
    const userEmail = userId || user?.email || user?.uid || 'anonymous';

    // Prepare FormData for multipart/form-data upload
    const formData = new FormData();

    // Add audio file - use the blob's original type or default to wav
    // MediaRecorder typically creates webm or mp4, but backend expects wav filename
    const audioFileType = audioBlob.type || 'audio/wav';
    const audioFile = new File([audioBlob], 'recording.wav', {
      type: audioFileType,
    });
    formData.append('audio_file', audioFile);

    // Add voice config as JSON string
    const voiceConfig = {
      language_code: 'en-US',
      model: 'chirp_2',
      encoding: 'AUTO',
      enable_automatic_punctuation: true,
    };
    formData.append('voice_config', JSON.stringify(voiceConfig));

    try {
      const response = await fetch(API_ENDPOINTS.CHAT_VOICE, {
        method: 'POST',
        headers: {
          'user-id': userEmail,
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [ChatSessionService] Voice transcription failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
        throw new Error(
          `Voice transcription failed: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      if (result.success && result.transcript) {
        return result.transcript;
      } else {
        const errorMsg = result.error || 'Unknown error';
        console.error('❌ [ChatSessionService] Transcription error:', errorMsg);
        throw new Error(`Transcription failed: ${errorMsg}`);
      }
    } catch (error) {
      console.error(
        '❌ [ChatSessionService] Voice transcription error:',
        error
      );
      throw error;
    }
  }
}
